import { AssetStatus, AssetStatusLabel } from '@/types';
import { ASSET_FIELDS } from '@/utils/mappings.util';

// Re-export for backward compatibility
export { ASSET_FIELDS };

/**
 * Asset status code to label mapping with fallbacks.
 *
 * Expected status codes from backend:
 * - 'A': Active
 * - 'P': Pending
 * - 'U': Inactive
 * - 'I': Expiring
 * - 'F': Frozen
 * - 'D': Deleted
 * - 'FR': Rejected
 * - 'AP': Active Passive
 * - 'AL': Active Low
 */
export const ASSET_STATUS_CODE_MAP: Record<string, string> = {
  A: 'Active',
  P: 'Pending',
  U: 'Inactive',
  I: 'Expiring',
  F: 'Frozen',
  D: 'Deleted',
  FR: 'Rejected',
  AP: 'Passive Asset Enumeration',
  AL: 'Active Asset Enumeration',
};

/**
 * Converts an asset status code to a user-friendly label.
 *
 * This function handles multiple formats:
 * - Single/double character codes: 'A', 'P', 'FR', etc.
 * - Full status strings: 'ACTIVE', 'PENDING', etc.
 * - AssetStatus enum values
 *
 * @param status - The status code or string
 * @returns The formatted status label
 */
export const getAssetStatusLabel = (status: string | AssetStatus): string => {
  if (!status) {
    return 'Unknown';
  }

  // Handle AssetStatus enum values
  if (status in AssetStatusLabel) {
    return AssetStatusLabel[status as AssetStatus];
  }

  // Handle status codes
  if (ASSET_STATUS_CODE_MAP[status]) {
    return ASSET_STATUS_CODE_MAP[status];
  }

  // Handle full status strings (fallback)
  const fullStatusMap: Record<string, string> = {
    ACTIVE: 'Active',
    PENDING: 'Pending',
    INACTIVE: 'Inactive',
    EXPIRING: 'Expiring',
    FROZEN: 'Frozen',
    DELETED: 'Deleted',
    REJECTED: 'Rejected',
  };

  const upperStatus = status.toUpperCase();
  if (fullStatusMap[upperStatus]) {
    return fullStatusMap[upperStatus];
  }

  // Return the original status if no mapping found
  return status;
};

/**
 * Short human-readable status labels for timeline and history display.
 *
 * Differs from AssetStatusLabel (action-oriented: "Freeze Asset") and
 * ASSET_STATUS_CODE_MAP (verbose: "Passive Asset Enumeration").
 * These are concise labels suitable for DashedTag display in timelines.
 */
export const ASSET_STATUS_DISPLAY_LABEL: Record<string, string> = {
  AP: 'Passive',
  AL: 'Active (Low)',
  A: 'Active',
  F: 'Frozen',
  D: 'Deleted',
  P: 'Pending',
};

/**
 * Get a CSS class name for asset status styling
 *
 * @param status - The asset status
 * @returns CSS class name for styling
 */
export const getAssetStatusClass = (status: string | AssetStatus): string => {
  const label = getAssetStatusLabel(status).toLowerCase();

  const statusClasses: Record<string, string> = {
    active: 'text-green-600 bg-green-50',
    pending: 'text-yellow-600 bg-yellow-50',
    inactive: 'text-chariot-text-secondary bg-layer1',
    expiring: 'text-orange-600 bg-orange-50',
    frozen: 'text-blue-600 bg-blue-50',
    deleted: 'text-red-600 bg-red-50',
    rejected: 'text-red-600 bg-red-50',
  };

  return statusClasses[label] || 'text-chariot-text-secondary bg-layer1';
};
